import { ampli } from '@/ampli';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useClickAway, useMedia } from 'react-use';
import { useDebounce } from 'react-use';

import { solana } from '@/constants/chain';

import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import {
  pastedReceivingWalletAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { buttons } from '@/styles/buttons';
import { iosSafeInputText } from '@/styles/input';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { AlertCircleIcon } from '@/ui/Icons/AlertCircleIcon';
import { BottomPanelModal } from '@/ui/Modal/BottomPanelModal';
import { Popover } from '@/ui/Popover';

import {
  ChainAddress,
  isChainAddress,
  isEthereumAddress,
  isEthereumWalletAddress,
  isSolanaAddress,
  isSolanaWalletAddress,
} from '@/utils/models';

interface Props {
  /** Controls the visibility of the modal */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Function to toggle the visibility of the modal */
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  /** The chain id of the wallet to connect */
  chainId: number | undefined;
  /** The desktop popover class */
  popoverClass: string;
}

export const PasteOrConnectWalletModal = ({
  isOpen,
  onOpenChange,
  onClose,
  chainId,
  popoverClass,
}: Props) => {
  const isMobile = useMedia(breakpoints.mobile, false);
  const { pastedReceivingWalletAddress, setPastedReceivingWalletAddress } =
    useMarketTradeSettingsStore(pastedReceivingWalletAddressSelector);
  const { connect } = useMatchaWallets();
  const [address, setAddress] = useState<string | undefined>();
  const [debouncedAddress, setDebouncedAddress] = useState<ChainAddress | undefined>();
  const [addressError, setAddressError] = useState<string | undefined>();
  const popoverRef = useRef<HTMLDivElement>(null!);
  const inputRef = useRef<HTMLInputElement>(null!);
  const abortControllerRef = useRef<AbortController | null>(null);

  const isSolana = chainId === solana.id;

  // Close the popover when the user clicks outside of it
  useClickAway(popoverRef, () => {
    if (!isOpen || isMobile) return;
    onOpenChange(false);
  });

  useDebounce(
    async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      if (!address || !chainId) {
        setDebouncedAddress(undefined);
        setAddressError(undefined);
        return;
      }

      try {
        let isValid = false;

        if (isSolana) {
          isValid = await isSolanaWalletAddress(address);
        } else {
          isValid = await isEthereumWalletAddress(address, chainId);
        }

        if (abortController.signal.aborted) {
          return;
        }

        if (!isValid) {
          setDebouncedAddress(undefined);
          setAddressError(
            isSolana ? 'Invalid Solana wallet address' : 'Invalid Ethereum wallet address',
          );
        } else {
          setDebouncedAddress(address as ChainAddress);
          setAddressError(undefined);
        }
      } catch {
        if (abortController.signal.aborted) {
          return;
        }
        setDebouncedAddress(undefined);
        setAddressError('Error validating address');
      }
    },
    250,
    [address, chainId],
  );

  const resetAddresses = useCallback(() => {
    setAddress(undefined);
    setDebouncedAddress(undefined);
    setAddressError(undefined);
    setPastedReceivingWalletAddress(undefined);
  }, [setPastedReceivingWalletAddress]);

  useEffect(() => {
    if (!chainId) return;
    // Reset the address when is not compatible with the chainId
    if (
      !pastedReceivingWalletAddress ||
      (isSolana && !isSolanaAddress(pastedReceivingWalletAddress)) ||
      (!isSolana && !isEthereumAddress(pastedReceivingWalletAddress))
    ) {
      resetAddresses();
    }
  }, [pastedReceivingWalletAddress, chainId, resetAddresses, isSolana]);

  useEffect(() => {
    if (!isOpen) return;
    // Update the address when the modal opens
    setAddress(pastedReceivingWalletAddress);
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen, pastedReceivingWalletAddress]);

  const onConfirmClick = () => {
    if (!debouncedAddress || !isChainAddress(debouncedAddress)) return;
    setPastedReceivingWalletAddress(debouncedAddress as ChainAddress);
    onClose();
  };

  const onConnectWalletClick = () => {
    ampli.walletConnectClicked({ source: 'trade_module', ui_location: 'cross_chain_trade_module' });
    connect();
    resetAddresses();
    onClose();
  };

  const content = (
    <div className="p-2 pt-8 md:pt-2 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <div className="flex items-center flex-grow">
            <input
              ref={inputRef}
              className={fontStyles({
                variant: 'LABEL_MD',
                class: [
                  'bg-token-alphalight-200',
                  'relative',
                  'w-full',
                  'h-9',
                  'rounded-sm',
                  'border-0',
                  'px-3',
                  'transition-all',
                  'duration-200',
                  'ease-in-out',
                  '[&::-webkit-search-cancel-button]:hidden',
                  'placeholder:text-token-gray-700',
                  'focus:outline-token-purple-900',
                  'focus:outline-2',
                  'focus-visible:outline-token-purple-900',
                  'focus-visible:outline-2',
                  'hover:outline-token-alphalight-500',
                  iosSafeInputText.md,
                  'hover:outline-1',
                  'data-[is-invalid="true"]:outline-token-red-900',
                  'data-[is-invalid="true"]:outline-2',
                  'data-[is-invalid="true"]:pr-8',
                  'data-[is-invalid="true"]:-mr-6',
                ],
              })}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={
                isSolana ? 'Paste Solana wallet address...' : 'Paste Ethereum wallet address...'
              }
              type="text"
              spellCheck="false"
              autoComplete="off"
              autoCorrect="off"
              data-is-invalid={!!addressError}
            />
            {addressError && (
              <span className="text-token-red-900 w-4 h-4">
                <AlertCircleIcon />
              </span>
            )}
          </div>
          {debouncedAddress && !addressError && (
            <button
              className={buttons({
                color: 'purple',
                size: 'small',
                shape: 'rounded',
              })}
              onClick={onConfirmClick}
              disabled={!!addressError}
            >
              Confirm
            </button>
          )}
        </div>
        {addressError && (
          <div
            className={fontStyles({ variant: 'LABEL_MD', class: ['px-1', 'text-token-red-900'] })}
          >
            {addressError}
          </div>
        )}
      </div>

      <div className="w-full h-px bg-[linear-gradient(to_right,transparent_0%,var(--color-token-gray-300)_10%,var(--color-token-gray-300)_90%,transparent_100%)]" />

      <button
        className={buttons({
          color: 'white',
          size: 'medium',
          shape: 'pill',
        })}
        onClick={onConnectWalletClick}
      >
        Connect Wallet
      </button>
    </div>
  );

  return isMobile ? (
    <BottomPanelModal isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose} variant="LARGE">
      {content}
    </BottomPanelModal>
  ) : isOpen ? (
    <Popover className={popoverClass} popoverRef={popoverRef} isOpen={isOpen} onClose={onClose}>
      {content}
    </Popover>
  ) : null;
};
